import Search from '@mui/icons-material/Search';
import {
  Box,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Link,
  DialogContent,
} from '@mui/material';
import { t } from 'i18next';
import AccountExpandableListItems from './AccountExpandableListItems';
import AccountTypeCard from './AccountTypeCard';
import AddAccountProgressBar from '../shared/AddAccountProgressBar';
import ModalFooter from '../shared/ModalFooter';
import ModalSectionTitle from '../shared/ModalSectionTitle';
import { StepsForManual } from '../useAddAccountNavigation';
import { useMemo } from 'react';
import { getManualAccountTypes } from '../accountTypeData';
import { Classification } from '@/store/classificationsApi';
import useCategorySearch from './useCategorySearch';
import CategoryUtility, {
  ClassificationGroup,
  ClassificationItem,
} from '../categories.utility';

interface ManualSelectAccountTypeProps {
  handleOnCancel: () => void;
  updateModalStep: (step: StepsForManual) => void;
  footerLabels: any;
  classifications: Classification[];
  accountTypes: ClassificationGroup[];
  selectedClassification: ClassificationItem | null;
  handleClassificationClick: (classification: ClassificationItem) => void;
  handleCategoryClick: (name: string) => void;
  openCategories: Record<string, boolean>;
}

const ManualSelectAccountType = ({
  handleOnCancel,
  updateModalStep,
  footerLabels,
  classifications,
  accountTypes,
  selectedClassification,
  handleClassificationClick,
  handleCategoryClick,
  openCategories,
}: ManualSelectAccountTypeProps) => {
  const manualAccountTypes = useMemo(() => {
    return getManualAccountTypes(classifications);
  }, [classifications]);

  const { searchTerm, searchTermResults, handleSearchTermOnChange } =
    useCategorySearch(accountTypes);

  const nextStep = useMemo(() => {
    if (
      CategoryUtility.isRealEstate(
        selectedClassification?.ClassificationId || 0,
      )
    ) {
      return StepsForManual.MANUAL_REAL_ESTATE_DETAILS;
    }

    return StepsForManual.MANUAL_ACCOUNT_DETAILS;
  }, [selectedClassification?.ClassificationId]);

  return (
    <>
      <DialogContent>
        <Box sx={{ marginBottom: 4 }}>
          <AddAccountProgressBar
            activeStep={StepsForManual.MANUAL_ACCOUNT_TYPE}
          />
          <ModalSectionTitle>
            {t('addAccountModal.selectAccountType.title')}
          </ModalSectionTitle>
          <Link
            underline='hover'
            onClick={handleOnCancel}
            sx={{ fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}
          >
            {t('addAccountModal.selectAccountType.addOnlineAccount')}
          </Link>
        </Box>

        <Box sx={{ marginBottom: 4 }}>
          <Grid
            container
            columnSpacing={3}
            rowSpacing={3}
          >
            {manualAccountTypes.map((item) => {
              return (
                <Grid
                  size={{ xs: 12, sm: 6 }}
                  key={item.uiKey ?? String(item.ClassificationId)}
                >
                  <AccountTypeCard
                    accountType={item}
                    selectedClassification={selectedClassification}
                    handleClassificationClick={handleClassificationClick}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Box sx={{ marginBottom: 4 }}>
          <Box sx={{ marginBottom: 2 }}>
            <Grid
              container
              rowSpacing={3}
              columnSpacing={3}
              sx={{ alignItems: 'flex-end' }}
            >
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography
                  variant='subtitle1'
                  component='h4'
                >
                  {t('addAccountModal.selectAccountType.otherAccountTypes')}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  variant='outlined'
                  value={searchTerm}
                  onChange={(e) => handleSearchTermOnChange(e.target.value)}
                  placeholder={t('addAccountModal.selectAccountType.search')}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position='start'>
                          <Search />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ height: 200, overflow: 'auto' }}>
            {searchTermResults?.map((item) => (
              <List
                disablePadding
                key={item.ClassificationId}
              >
                <ListItemButton
                  sx={{ pl: 6 }}
                  selected={
                    selectedClassification?.ClassificationId ===
                    item.ClassificationId
                  }
                  onClick={() => handleClassificationClick(item)}
                >
                  <ListItemText primary={item.Name} />
                </ListItemButton>
                <Divider />
              </List>
            ))}

            {searchTerm && searchTermResults.length === 0 && (
              <Typography>
                {t('addAccountModal.selectAccountType.noMatch')}
              </Typography>
            )}

            {!searchTerm && (
              <List>
                <AccountExpandableListItems
                  accountTypes={accountTypes}
                  openCategories={openCategories}
                  handleCategoryClick={handleCategoryClick}
                  selectedClassification={selectedClassification}
                  handleClassificationClick={handleClassificationClick}
                />
              </List>
            )}
          </Box>
        </Box>
      </DialogContent>

      <ModalFooter
        onCancel={handleOnCancel}
        onPrimaryAction={() => updateModalStep(nextStep)}
        isPrimaryActionDisabled={!selectedClassification}
        label={{ ...footerLabels, primaryCta: footerLabels.continue }}
      />
    </>
  );
};

export default ManualSelectAccountType;
